// behavior-tag-affordance.ts — PSTUDIO-4229

/** One chip drawn by a CLOSED Behaviors cell. */
export interface ClosedBehaviorsCellTag {
  /** The stored token this chip stands for — also its React key. */
  value: string;
  /** What the chip prints. */
  label: string;
  /** The "+N" chip that stands for every value past the visible limit. */
  isOverflowTag: boolean;
  /**
   * Whether the chip carries a destructive remove control (the DS cross).
   *
   * Typed `false`, not `boolean` (PSTUDIO-4229): the rule below is that a chip
   * in a CLOSED cell never offers one, so widening this type is what a change of
   * that rule looks like in a diff.
   *
   * What the literal type CATCHES is narrow, and was measured with an isolated
   * `tsc` rather than assumed: a comparison against the other value
   * (`tag.removable === true`) and an assignment of `true` fail to compile
   * (TS2367 / TS2322), while a branch that merely READS it — `if (tag.removable)
   * { … }`, `tag.removable ? handler : undefined`, an `onRemove` handed over
   * beside it — compiles cleanly. So the type is a marker, not the guard, and
   * the docblock must not be read as one.
   *
   * The line that actually keeps the cross off the chip is in the DS element:
   * `ps-tag`'s render draws the remove `ps-icon-button` only inside
   * `this.removable ? … : ''` (`design-system/src/components/tag/tag.wc.ts`), so
   * with `removable` false no remove control exists in the shadow root at all.
   */
  removable: false;
}

/**
 * How many real values a closed cell prints before it collapses the rest into
 * the "+N" chip. The chip occupies the slot AFTER them, so a cell holding five
 * values prints four labels and "+ 1".
 */
export const CLOSED_BEHAVIORS_CELL_VISIBLE_TAGS = 4;

/**
 * PSTUDIO-4229: the chips a CLOSED Behaviors cell draws, and what each of them
 * may do.
 *
 * A chip's remove cross is positioned against the chip's own right edge: these
 * chips render `size="small"`, so the rule that applies is
 * `:host([size='small']) .c-tag__remove { inset-inline-end: spacing(xsmall) }`
 * (`design-system/src/components/tag/tag.scss`; the base rule there is
 * `spacing(medium)`). The column is NOT a fixed width — 235px is its MINIMUM
 * (`table/table.tsx` `minSize: 235`, over a `defaultColumn` of `size: 150,
 * maxSize: 500`) and `columnResizeMode: 'onChange'` lets the user drag it
 * wider, with the result persisted in `tableStore.columnsSizes`. So where the
 * cross falls relative to the cell's centre is a FUNCTION of the current width,
 * not a constant — which STRENGTHENS the rule below rather than weakening it:
 * at no width may a destructive control sit where the ordinary click lands.
 * Measured BEFORE the fix on UAT at the default width, cell centre x = 1703.5:
 * "Inline Webview" (chip 117.9px) put the cross at 1696.9..1712.9 and "Disable
 * free text" (122.5px) at 1701.5..1717.5, both over the centre. An ordinary
 * click meant to OPEN the cell therefore landed on the cross, whose handler
 * writes the row immediately (`writeBehaviors` -> `updateRow({ saveImmediately:
 * true })`), so the value was gone from the server before the cell ever opened
 * and the user had edited nothing. A longer label moved the cross clear of the
 * centre ("Transfer to live agent", 147.3px, 22.8px to the right), which is the
 * only reason the other four tokens looked healthy at that one width.
 *
 * So the rule is about the STATE, not about any label or width: a chip in a
 * closed cell is a read-only summary and carries no destructive control at all.
 * Removing a behavior stays available where it is a deliberate act — inside the
 * opened `ps-select`, by unticking the option.
 */
export function buildClosedBehaviorsCellTags(
  values: readonly string[] | null | undefined,
  getLabel: (value: string) => string
): ClosedBehaviorsCellTag[] {
  if (!values) return [];

  const tags: ClosedBehaviorsCellTag[] = [];

  values.forEach((value, index) => {
    if (index > CLOSED_BEHAVIORS_CELL_VISIBLE_TAGS) return;

    const isOverflowTag = index === CLOSED_BEHAVIORS_CELL_VISIBLE_TAGS;

    tags.push({
      value,
      label: isOverflowTag
        ? `+ ${values.length - CLOSED_BEHAVIORS_CELL_VISIBLE_TAGS}`
        : getLabel(value),
      isOverflowTag,
      // PSTUDIO-4229: never — a closed cell is a read-only summary. See the
      // docblock above for why this cannot be decided per label or per width.
      removable: false,
    });
  });

  return tags;
}

/** The `pro-studio/workflows` keys a closed Behaviors cell names itself with. */
export const CLOSED_BEHAVIORS_CELL_NAME_KEYS = {
  /** No value in the cell. */
  empty: 'behaviors_cell.open',
  /** Interpolates `values` — the whole list, already formatted for the locale. */
  withValues: 'behaviors_cell.open_with_values',
} as const;

export interface ClosedBehaviorsCellName {
  /** Key inside the `pro-studio/workflows` namespace. */
  key: (typeof CLOSED_BEHAVIORS_CELL_NAME_KEYS)[keyof typeof CLOSED_BEHAVIORS_CELL_NAME_KEYS];
  /** i18next interpolation options, absent for the empty case. */
  options?: { values: string };
}

/**
 * PSTUDIO-4229: what a CLOSED Behaviors cell calls itself.
 *
 * Returns a KEY plus interpolation options rather than a sentence: the wrapper
 * is `role="button"` with an `aria-label`, and an `aria-label` REPLACES the name
 * computed from the element's contents, so this string is the only thing a
 * screen reader gets from the cell. Building it by concatenating an English
 * prefix onto the values fixes English word order and English punctuation into
 * the app, which `ar` (RTL) and `zh` cannot follow — `.claude/rules/
 * translations.md`. With the key carrying the whole phrase, a locale moves the
 * values wherever its grammar puts them.
 *
 * The values are joined by the CALLER's `formatList`, not here, for the same
 * reason: a list separator is a locale's decision (`zh` uses `、`), and the
 * caller is the layer that knows the active language.
 *
 * The FULL list is named, not the four chips plus "+ N": "+ 2" tells a screen
 * reader user nothing about what those two are.
 *
 * NOT covered by this: the behavior LABELS themselves. `behaviorOptionsMap` in
 * `node-behaviors-multi-select.tsx` is hardcoded English and pre-dates this
 * ticket; localizing that map is known debt, deliberately left out of
 * PSTUDIO-4229 rather than fixed halfway.
 */
export function buildClosedBehaviorsCellName(
  values: readonly string[] | null | undefined,
  getLabel: (value: string) => string,
  formatList: (labels: string[]) => string
): ClosedBehaviorsCellName {
  if (!values || values.length === 0) {
    return { key: CLOSED_BEHAVIORS_CELL_NAME_KEYS.empty };
  }

  return {
    key: CLOSED_BEHAVIORS_CELL_NAME_KEYS.withValues,
    options: { values: formatList(values.map((value) => getLabel(value))) },
  };
}

/**
 * Joins the value labels of a closed Behaviors cell for one language.
 *
 * `Intl.ListFormat` is the part that knows a list separator is not universal.
 * `{ type: 'conjunction', style: 'narrow' }` is the pairing that gives a plain
 * enumeration with the locale's own separator and no "and" — measured on this
 * runtime: `en` and `ru` answer `A, B, C`, `zh` answers `A、B、C`, `ar` answers
 * `A وB وC`. (`type: 'unit'` looks like the right name and is not: it joins
 * `zh` with nothing at all — `ABC` — and `ru` with spaces.)
 *
 * The `catch` is not defensive decoration: `Intl.ListFormat` throws `RangeError`
 * on a malformed language tag, and this runs inside a table cell's render, where
 * a throw takes the whole grid to the app error boundary. The fallback is the
 * English-shaped join, i.e. exactly what the code did before this ticket.
 */
export function formatBehaviorLabelList(
  labels: string[],
  language: string
): string {
  try {
    return new Intl.ListFormat(language, {
      style: 'narrow',
      type: 'conjunction',
    }).format(labels);
  } catch {
    return labels.join(', ');
  }
}
